import styled from "styled-components";
import { AlertCircle, AlertTriangle, CheckCircle2, Info } from "lucide-react";
import colors from "@styling/colors";
import { borderRadius, spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";

type InfoBoxVariant = "error" | "success" | "info" | "warning";

type InfoBoxProps = {
  variant: InfoBoxVariant;
  children: React.ReactNode;
  className?: string;
};

const getVariantColors = (variant: InfoBoxVariant) => {
  switch (variant) {
    case "error":
      return {
        color: colors.dangerText,
        backgroundColor: colors.dangerBackground,
        accentColor: colors.danger,
      };
    case "success":
      return {
        color: colors.successText,
        backgroundColor: colors.successBackground,
        accentColor: colors.success,
      };
    case "info":
      return {
        color: colors.infoText,
        backgroundColor: colors.infoBackground,
        accentColor: colors.info,
      };
    case "warning":
      return {
        color: colors.warningText,
        backgroundColor: colors.warningBackground,
        accentColor: colors.warning,
      };
  }
};

const VariantIcon = ({
  variant,
  color,
}: {
  variant: InfoBoxVariant;
  color: string;
}) => {
  const iconProps = { size: 18, color, strokeWidth: 2, "aria-hidden": true };
  switch (variant) {
    case "error":
      return <AlertCircle {...iconProps} />;
    case "success":
      return <CheckCircle2 {...iconProps} />;
    case "info":
      return <Info {...iconProps} />;
    case "warning":
      return <AlertTriangle {...iconProps} />;
  }
};

const StyledInfoBox = styled.div<{ $variant: InfoBoxVariant }>`
  display: flex;
  align-items: flex-start;
  gap: ${spacing.x2}px;
  padding: ${spacing.x2}px ${spacing.x2}px ${spacing.x2}px ${spacing.x3}px;
  background-color: ${({ $variant }) =>
    getVariantColors($variant).backgroundColor};
  color: ${({ $variant }) => getVariantColors($variant).color};
  border-radius: ${borderRadius.regular}px;
  border: 1px solid
    ${({ $variant }) => getVariantColors($variant).accentColor}33;
  border-left: 4px solid
    ${({ $variant }) => getVariantColors($variant).accentColor};
  margin: 0;
  font-size: ${fontSizes.small + 2}px;
  line-height: 1.5;
`;

const IconWrap = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  margin-top: 1px;
`;

const Content = styled.div`
  flex: 1;
  min-width: 0;
`;

export const InfoBox = ({ variant, children, className }: InfoBoxProps) => {
  const variantColors = getVariantColors(variant);
  return (
    <StyledInfoBox $variant={variant} className={className}>
      <IconWrap>
        <VariantIcon variant={variant} color={variantColors.accentColor} />
      </IconWrap>
      <Content>{children}</Content>
    </StyledInfoBox>
  );
};
